import { FormatToShortPriceTemplate } from '@utils/FormatValue';
import { ChartData, ChartOptions } from 'chart.js';
import pattern from 'patternomaly';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { IMaterialExposure } from '../../../sp-types/netExposureType';

const colors = { FUTURE_ORD: '#4cd292', INVENTORY: '#3693ff', FINANCIAL: '#cf24b4' };

interface BalanceChartCardProps {
  title: string;
  balanceDate: string;
  materialExposureData: IMaterialExposure;
  netExposure: number;
  exposureColor?: string;
}

const BalanceChartCard: React.FC<BalanceChartCardProps> = ({
  title,
  balanceDate,
  materialExposureData: { futureOrder, inventorySum, financialPositions },
  netExposure,
  exposureColor,
}) => {
  const balanceValue = {
    label: 'Balance Amount',
    data: [0, -netExposure],

    backgroundColor: ['white', pattern.draw('diagonal', 'rgba(255, 148, 70, 0.5)', '20', 4)],
    borderColor: ['#ff9446'],

    barThickness: 25,
  };

  const data: ChartData<'bar'> = {
    labels: ['Future Orders', 'Inv & Positions'],
    datasets: [
      {
        label: 'Future orders',
        data: [futureOrder, 0],
        backgroundColor: colors.FUTURE_ORD,
        barThickness: 25,
      },
      ...(netExposure !== 0 ? [balanceValue] : []),
      {
        label: 'Inventory',
        data: [0, inventorySum],
        backgroundColor: colors.INVENTORY,
        barThickness: 25,
      },
      {
        label: 'Positions',
        data: [0, financialPositions],
        backgroundColor: colors.FINANCIAL,
        barThickness: 25,
      },

      // ...(netExposure > 0 ? [balanceValue] : []),
    ],
  };

  const options: ChartOptions<'bar'> = {
    scales: {
      x: { stacked: true, grid: { offset: true, display: false } },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'USD',
        },
        ticks: { callback: (value) => FormatToShortPriceTemplate(+value) },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    layout: {},
  };

  const equationData: IEquation[] = [
    {
      text: 'Inventory',
      operation: '',
      color: 'blue-350',
      value: FormatToShortPriceTemplate(inventorySum),
    },
    {
      text: 'Future orders',
      operation: '-',
      color: 'green-350',
      value: FormatToShortPriceTemplate(futureOrder),
    },
    {
      text: 'Financial Positions',
      operation: '+',
      color: 'pink-350',
      value: FormatToShortPriceTemplate(financialPositions),
    },
  ];

  return (
    <div className="flex flex-col border-2 border-gray-100 rounded-lg gap-3 p-4 justify-between min-w-[28rem]">
      <div className="flex justify-between items-center">
        <div className="text-red text-lg">{title}</div>
        <div className="text-gray text-sm">{balanceDate}</div>
      </div>
      <div className="grid grid-cols-6  space-y-2 max-w-[19rem]">
        {equationData.map((item, index) => (
          <React.Fragment key={'a' + index}>
            <div className="col-span-3 space-x-2 mt-auto ">
              <span className={`inline-block w-2 h-2 bg-${item.color} rounded-sm`}></span>
              <span className="text-sm">{item.text}</span>
            </div>
            <div className="col-span-1 ">{item.operation}</div>
            <div className="text-lg col-span-2 text-start content-end">{item.value}</div>
          </React.Fragment>
        ))}

        <div className="col-span-3 col-start-4">
          <div style={{ color: exposureColor }} className={`border-t border-gray-100 text-lg pt-2 text-center`}>
            {FormatToShortPriceTemplate(netExposure)}
          </div>
        </div>
      </div>
      <div className="flex justify-end w-64"></div>
      <div className="flex w-full justify-center">
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

export default BalanceChartCard;

interface IEquation {
  text: string;
  color: string;
  operation: string;
  value: string;
}
